import { DownOutlineIcon, VOutlineIcon } from '@carboncopyco/icons';
import { useBoolean, useClickAway } from 'ahooks';
import classNames from 'classnames';
import React, { useRef } from 'react';
import { ColorHex } from '../../constants';
import { DropdownMenu, DropdownMenuItem } from '../DropdownMenu';
import { TooltipBubble } from '../TooltipBubble';
import { Capsule } from './Capsule';

export interface CapsuleDropdownItem<T> {
  label: string;
  id: T;
  icon?: React.ReactElement;
  disabled?: boolean;
}

export type CapsuleDropdownProps<T> = CapsuleDropdownContentProps<T> & {
  small?: boolean;
  scroll?: boolean;
  ellipsis?: boolean;
  icon?: React.ReactElement;
  showLabel?: boolean;
  disabled?: boolean;
};
export type CapsuleDropdownContentProps<T> = {
  value: T;
  onSelect: (id: T) => void;
  items: CapsuleDropdownItem<T>[];
};

export function CapsuleDropdown<T extends string>({
  value,
  small,
  items,
  onSelect,
  scroll,
  ellipsis,
  icon,
  showLabel = true,
  disabled,
}: CapsuleDropdownProps<T>) {
  const [visible, { setFalse: hideDropdown, toggle: toggleDropdown }] =
    useBoolean(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickAway(hideDropdown, dropdownRef);

  const itemsMap = Object.fromEntries(items.map((item) => [item.id, item]));

  return (
    <div ref={dropdownRef}>
      <DropdownMenu
        disabled={disabled}
        scrollAuto={scroll}
        visible={visible}
        button={
          <Capsule
            selected={visible}
            onClick={() => toggleDropdown()}
            small={small}
            rightIcon={<DownOutlineIcon />}
            label={showLabel ? itemsMap[value].label : ''}
            ellipsis={ellipsis}
            leftIcon={icon}
          />
        }
      >
        <div className="min-w-40">
          {items.map((item) => (
            <div
              className={classNames({
                'max-w-ds-50': ellipsis,
              })}
              key={item.id}
            >
              <TooltipBubble
                placement="top"
                title={item.label}
                disabled={!ellipsis}
              >
                <DropdownMenuItem
                  disabled={item.disabled}
                  key={item.id}
                  iconRight={
                    value === item.id ? (
                      <VOutlineIcon color={ColorHex.natural} />
                    ) : undefined
                  }
                  onClick={() => {
                    hideDropdown();
                    onSelect(item.id);
                  }}
                  label={item.label}
                  iconLeft={
                    item.icon &&
                    React.cloneElement(item.icon, {
                      color: ColorHex.accent3100,
                    })
                  }
                />
              </TooltipBubble>
            </div>
          ))}
        </div>
      </DropdownMenu>
    </div>
  );
}
